import type { NodeTypeDef } from '../core/types';

/** Blend modes, shared by every node that composites. Index order is the GLSL contract. */
export const BLEND_MODES = [
  { value: 'copy', label: 'Copy' },
  { value: 'add', label: 'Add' },
  { value: 'subtract', label: 'Subtract' },
  { value: 'multiply', label: 'Multiply' },
  { value: 'divide', label: 'Divide' },
  { value: 'screen', label: 'Screen' },
  { value: 'overlay', label: 'Overlay' },
  { value: 'softlight', label: 'Soft Light' },
  { value: 'hardlight', label: 'Hard Light' },
  { value: 'darken', label: 'Darken' },
  { value: 'lighten', label: 'Lighten' },
  { value: 'difference', label: 'Difference' },
  { value: 'exclusion', label: 'Exclusion' },
  { value: 'dodge', label: 'Color Dodge' },
  { value: 'burn', label: 'Color Burn' },
  { value: 'linearburn', label: 'Linear Burn' },
  { value: 'vividlight', label: 'Vivid Light' },
  { value: 'pinlight', label: 'Pin Light' },
];

const WRAP_MODES = [
  { value: 'tile', label: 'Tile' },
  { value: 'clamp', label: 'Clamp' },
  { value: 'mirror', label: 'Mirror' },
  { value: 'clip', label: 'Clip to black' },
];

export const filters: NodeTypeDef[] = [
  {
    id: 'filter.blend',
    label: 'Blend',
    category: 'filter',
    description: 'Composite a foreground over a background with a blend mode and optional mask.',
    guidance:
      'The most-used node in any graph. Background is the base layer, Foreground is what goes on top. Mask restricts the effect — feeding a Noise into Mask is how you get natural, uneven wear.',
    keywords: ['mix', 'composite', 'layer', 'combine', 'over', 'multiply', 'mask'],
    inputs: [
      { name: 'background', label: 'Background', kind: 'color' },
      { name: 'foreground', label: 'Foreground', kind: 'color' },
      {
        name: 'mask',
        label: 'Mask',
        kind: 'grayscale',
        optional: true,
        // Unconnected mask means "apply everywhere", so white.
        fallback: [1, 1, 1, 1],
      },
    ],
    output: 'passthrough',
    passthroughFrom: 'background',
    params: [
      { name: 'mode', label: 'Mode', type: 'enum', default: 'copy', options: BLEND_MODES },
      { name: 'opacity', label: 'Opacity', type: 'float', default: 1, min: 0, max: 1, step: 0.001 },
      {
        name: 'invertMask',
        label: 'Invert Mask',
        type: 'bool',
        default: false,
        group: 'Mask',
      },
      {
        name: 'clampOutput',
        label: 'Clamp Output',
        type: 'bool',
        default: true,
        group: 'Output',
        help: 'Additive modes can exceed 1. Off preserves the overshoot for later nodes.',
      },
    ],
    shader: /* glsl */ `
      vec4 texdir(vec2 uv) {
        vec4 bg = in0(uv);
        vec4 fg = in1(uv);
        float mask = in2g(uv);
        if (p_invertMask) mask = 1.0 - mask;

        vec3 blended = blend3(p_mode, bg.rgb, fg.rgb);
        float amount = saturate(p_opacity * mask * fg.a);
        vec3 rgb = mix(bg.rgb, blended, amount);
        float a = mix(bg.a, max(bg.a, fg.a), amount);
        if (p_clampOutput) rgb = saturate(rgb);
        return vec4(rgb, a);
      }
    `,
  },

  {
    id: 'filter.blur',
    label: 'Blur',
    category: 'filter',
    description: 'Separable Gaussian blur.',
    guidance:
      'Softens anything. Blurring a height map before Normal is the standard way to control how sharp the resulting surface reads. Large radii are cheap here because the blur is separable.',
    keywords: ['gaussian', 'soften', 'smooth', 'defocus'],
    inputs: [{ name: 'input', label: 'Input', kind: 'color' }],
    output: 'passthrough',
    params: [
      {
        name: 'radius',
        label: 'Radius',
        type: 'float',
        default: 8,
        min: 0,
        max: 128,
        step: 0.1,
        help: 'In pixels at 512px, and held to that size as the document resolution changes.',
      },
      {
        name: 'quality',
        label: 'Quality',
        type: 'int',
        default: 16,
        min: 4,
        max: 48,
        group: 'Quality',
        help: 'Taps per axis. Raise if you see banding on a wide blur.',
      },
    ],
    // Pass 0 blurs horizontally, pass 1 blurs the result vertically.
    passes: { kind: 'fixed', count: 2 },
    shader: /* glsl */ `
      vec4 texdir(vec2 uv) {
        if (p_radius < 0.01) return uPass == 0 ? in0(uv) : prev(uv);

        vec2 px = pixelUnit();
        vec2 dir = uPass == 0 ? vec2(px.x, 0.0) : vec2(0.0, px.y);
        int taps = p_quality;
        float sigma = max(p_radius, 0.001) / 2.0;
        // Step so the kernel spans the full radius regardless of tap count.
        float stepPx = p_radius / float(taps);

        vec4 sum = vec4(0.0);
        float wsum = 0.0;
        // The bound is constant because GLSL needs one; \`break\` past the tap
        // count keeps the actual work proportional to the chosen quality
        // rather than always paying for the maximum.
        for (int i = -48; i <= 48; i++) {
          if (i < -taps) continue;
          if (i > taps) break;
          float d = float(i) * stepPx;
          float w = exp(-(d * d) / (2.0 * sigma * sigma));
          vec2 o = dir * d;
          sum += (uPass == 0 ? in0l(uv + o) : prevl(uv + o)) * w;
          wsum += w;
        }
        return sum / max(wsum, 1e-6);
      }
    `,
  },

  {
    id: 'filter.blurDirectional',
    label: 'Directional Blur',
    category: 'filter',
    description: 'Blur along a single axis.',
    guidance:
      'Motion streaks, brushed metal, anisotropic highlights. Feed a Noise through this at low angle and it becomes fine brushed grain.',
    keywords: ['motion', 'streak', 'anisotropic', 'brushed', 'linear'],
    inputs: [{ name: 'input', label: 'Input', kind: 'color' }],
    output: 'passthrough',
    params: [
      {
        name: 'radius',
        label: 'Radius',
        type: 'float',
        default: 16,
        min: 0,
        max: 256,
        step: 0.1,
        help: 'Blur radius in pixels at 512px, held to the same material size at other resolutions.',
      },
      { name: 'angle', label: 'Angle', type: 'float', default: 0, min: -1, max: 1, step: 0.001 },
      {
        name: 'quality',
        label: 'Quality',
        type: 'int',
        default: 24,
        min: 4,
        max: 64,
        group: 'Quality',
      },
      {
        name: 'centered',
        label: 'Centered',
        type: 'bool',
        default: true,
        help: 'Off streaks in one direction only, which reads as motion.',
      },
    ],
    shader: /* glsl */ `
      vec4 texdir(vec2 uv) {
        if (p_radius < 0.01) return in0(uv);
        float a = p_angle * TAU;
        vec2 dir = vec2(cos(a), sin(a)) * pixelUnit();
        int taps = p_quality;
        vec4 sum = vec4(0.0);
        float wsum = 0.0;
        for (int i = 0; i <= 64; i++) {
          if (i > taps) break;
          float t = float(i) / float(taps);
          float d = p_centered ? (t * 2.0 - 1.0) * p_radius : t * p_radius;
          float w = p_centered ? exp(-t * t * 2.0) : 1.0 - t * 0.5;
          sum += in0l(uv + dir * d) * w;
          wsum += w;
        }
        return sum / max(wsum, 1e-6);
      }
    `,
  },

  {
    id: 'filter.warp',
    label: 'Warp',
    category: 'filter',
    description: 'Displace an image by the gradient of a warp map.',
    guidance:
      'The single best node for killing the "computer generated" look. Warp a regular pattern by a low-frequency Noise and it gains organic irregularity. Keep Intensity small — under 0.1 — for subtle results.',
    keywords: ['distort', 'displace', 'perturb', 'organic', 'wobble'],
    inputs: [
      { name: 'input', label: 'Input', kind: 'color' },
      { name: 'warp', label: 'Warp Map', kind: 'grayscale' },
    ],
    output: 'passthrough',
    passthroughFrom: 'input',
    params: [
      {
        name: 'intensity',
        label: 'Intensity',
        type: 'float',
        default: 0.05,
        min: -1,
        max: 1,
        step: 0.001,
      },
      {
        name: 'wrap',
        label: 'Wrap',
        type: 'enum',
        default: 'tile',
        options: WRAP_MODES,
        group: 'Sampling',
      },
    ],
    shader: /* glsl */ `
      vec4 texdir(vec2 uv) {
        // Central differences on the warp map give a displacement direction that
        // follows the map's slope, which is what makes the distortion look like
        // flow. Sampled a reference pixel apart rather than a texel apart: the
        // difference is never divided by the spacing, so a spacing that shrank
        // with the document would weaken the warp as the resolution rose.
        // Force interpolation for the analysis taps: under nearest sampling a
        // reference pixel is sub-texel at low document resolutions and all four
        // taps would otherwise collapse onto the centre pixel.
        vec2 px = pixelUnit();
        float l = in1gl(uv - vec2(px.x, 0.0));
        float r = in1gl(uv + vec2(px.x, 0.0));
        float d = in1gl(uv - vec2(0.0, px.y));
        float u = in1gl(uv + vec2(0.0, px.y));
        vec2 grad = vec2(r - l, u - d);
        return in0m(uv + grad * p_intensity, p_wrap);
      }
    `,
  },

  {
    id: 'filter.warpDirectional',
    label: 'Directional Warp',
    category: 'filter',
    description: 'Displace along a fixed angle, driven by a map.',
    guidance:
      "Where Warp follows the map's slope, this pushes everything one way by the map's brightness. Use it for drips, wood grain flow, and wind-blown sand.",
    keywords: ['drip', 'flow', 'slide', 'push', 'grain'],
    inputs: [
      { name: 'input', label: 'Input', kind: 'color' },
      { name: 'warp', label: 'Warp Map', kind: 'grayscale' },
    ],
    output: 'passthrough',
    passthroughFrom: 'input',
    params: [
      {
        name: 'intensity',
        label: 'Intensity',
        type: 'float',
        default: 0.1,
        min: -1,
        max: 1,
        step: 0.001,
      },
      { name: 'angle', label: 'Angle', type: 'float', default: 0.25, min: -1, max: 1, step: 0.001 },
      {
        name: 'bipolar',
        label: 'Bipolar',
        type: 'bool',
        default: true,
        help: 'Treats 0.5 as "no shift" so the map can push both ways.',
      },
      {
        name: 'wrap',
        label: 'Wrap',
        type: 'enum',
        default: 'tile',
        options: WRAP_MODES,
        group: 'Sampling',
      },
    ],
    shader: /* glsl */ `
      vec4 texdir(vec2 uv) {
        float w = in1g(uv);
        if (p_bipolar) w = w * 2.0 - 1.0;
        float a = p_angle * TAU;
        vec2 dir = vec2(cos(a), sin(a));
        return in0m(uv + dir * w * p_intensity, p_wrap);
      }
    `,
  },

  {
    id: 'filter.slopeBlur',
    label: 'Slope Blur',
    category: 'filter',
    description: 'Iteratively smear an image downhill along a slope map.',
    guidance:
      'Erosion in a box. Feed a hard-edged pattern as Input and a Noise as Slope, then raise Samples — the result looks weathered and rained-on in a way no single blur achieves.',
    keywords: ['erosion', 'weather', 'rain', 'drip', 'smear', 'melt'],
    inputs: [
      { name: 'input', label: 'Input', kind: 'color' },
      { name: 'slope', label: 'Slope Map', kind: 'grayscale' },
    ],
    output: 'passthrough',
    passthroughFrom: 'input',
    params: [
      {
        name: 'intensity',
        label: 'Intensity',
        type: 'float',
        default: 0.4,
        min: 0,
        max: 4,
        step: 0.01,
      },
      {
        name: 'samples',
        label: 'Samples',
        type: 'int',
        default: 8,
        min: 1,
        max: 32,
        help: 'Iterations. Each one is a full pass, so this is the cost knob.',
      },
      {
        name: 'mode',
        label: 'Mode',
        type: 'enum',
        default: 'blur',
        options: [
          { value: 'blur', label: 'Blur' },
          { value: 'min', label: 'Min' },
          { value: 'max', label: 'Max' },
        ],
      },
    ],
    // One pass per sample: each iteration walks the image a little further downhill.
    passes: { kind: 'parameter', parameter: 'samples', fallback: 1, minimum: 1 },
    shader: /* glsl */ `
      vec4 texdir(vec2 uv) {
        vec4 src = uPass == 0 ? in0(uv) : prev(uv);

        vec2 px = pixelUnit();
        float l = in1gl(uv - vec2(px.x, 0.0));
        float r = in1gl(uv + vec2(px.x, 0.0));
        float d = in1gl(uv - vec2(0.0, px.y));
        float u = in1gl(uv + vec2(0.0, px.y));
        vec2 grad = vec2(r - l, u - d);

        // Step downhill; the accumulated walk over N passes is the erosion path.
        vec2 offset = -grad * p_intensity * 4.0 * px * float(uPassCount);
        vec4 stepped = uPass == 0 ? in0l(uv + offset) : prevl(uv + offset);

        if (p_mode == K_mode_min) return min(src, stepped);
        if (p_mode == K_mode_max) return max(src, stepped);
        return mix(src, stepped, 0.5);
      }
    `,
  },

  {
    id: 'filter.transform',
    label: 'Transform 2D',
    category: 'filter',
    description: 'Rotate, scale, offset, and shear.',
    guidance:
      'Placement and repetition. Scale above 1 shrinks the content and repeats it (with Tile wrap); use Clip to place a single element on black.',
    keywords: ['rotate', 'scale', 'move', 'offset', 'shear', 'place', 'repeat'],
    inputs: [{ name: 'input', label: 'Input', kind: 'color' }],
    output: 'passthrough',
    params: [
      {
        name: 'scale',
        label: 'Scale',
        type: 'vec2',
        default: [1, 1],
        min: -16,
        max: 16,
        step: 0.001,
      },
      {
        name: 'rotation',
        label: 'Rotation',
        type: 'float',
        default: 0,
        min: -1,
        max: 1,
        step: 0.001,
      },
      {
        name: 'offset',
        label: 'Offset',
        type: 'vec2',
        default: [0, 0],
        min: -2,
        max: 2,
        step: 0.001,
      },
      {
        name: 'shear',
        label: 'Shear',
        type: 'vec2',
        default: [0, 0],
        min: -2,
        max: 2,
        step: 0.001,
        group: 'Advanced',
      },
      {
        name: 'pivot',
        label: 'Pivot',
        type: 'vec2',
        default: [0.5, 0.5],
        min: -1,
        max: 2,
        step: 0.001,
        group: 'Advanced',
      },
      {
        name: 'wrap',
        label: 'Wrap',
        type: 'enum',
        default: 'tile',
        options: WRAP_MODES,
        group: 'Sampling',
      },
    ],
    shader: /* glsl */ `
      vec4 texdir(vec2 uv) {
        // Inverse-map the destination pixel back into source space: scale up the
        // coordinate to scale the image down, which is the convention users expect.
        vec2 p = uv - p_pivot - p_offset;
        p = rot(-p_rotation * TAU) * p;
        p += vec2(p.y * p_shear.x, p.x * p_shear.y);
        vec2 s = vec2(
          abs(p_scale.x) < 1e-4 ? 1e-4 : p_scale.x,
          abs(p_scale.y) < 1e-4 ? 1e-4 : p_scale.y
        );
        p *= s;
        p += p_pivot;
        return in0m(p, p_wrap);
      }
    `,
  },

  {
    id: 'filter.tile',
    label: 'Tile',
    category: 'filter',
    description: 'Repeat the input on a grid, with optional per-tile randomisation.',
    guidance:
      'Fast repetition with variation. Unlike Transform, each copy can get its own rotation, brightness, and offset — enough to hide the repeat without a full Tile Sampler.',
    keywords: ['repeat', 'grid', 'array', 'pattern', 'stagger'],
    inputs: [{ name: 'input', label: 'Input', kind: 'color' }],
    output: 'passthrough',
    params: [
      { name: 'columns', label: 'Columns', type: 'int', default: 4, min: 1, max: 64 },
      { name: 'rows', label: 'Rows', type: 'int', default: 4, min: 1, max: 64 },
      {
        name: 'rowOffset',
        label: 'Row Offset',
        type: 'float',
        default: 0,
        min: 0,
        max: 1,
        step: 0.01,
        help: 'Stagger alternating rows, as in a brick bond. An odd count cannot alternate, so the shift accumulates instead and snaps to whole cells across the tile to stay seamless — which is why it steps.',
      },
      {
        name: 'columnOffset',
        label: 'Column Offset',
        type: 'float',
        default: 0,
        min: 0,
        max: 1,
        step: 0.01,
        help:
          'Stagger alternating columns. Combine with Row Offset for a woven layout.' +
          'An odd count cannot alternate, so the shift accumulates instead and snaps to whole cells across the tile to stay seamless — which is why it steps.',
      },
      {
        name: 'scale',
        label: 'Scale',
        type: 'float',
        default: 1,
        min: 0.05,
        max: 4,
        step: 0.01,
        group: 'Placement',
        help: 'Size within each cell. Below 1 leaves a gap between tiles.',
      },
      {
        name: 'rotation',
        label: 'Rotation',
        type: 'float',
        default: 0,
        min: -1,
        max: 1,
        step: 0.001,
        group: 'Placement',
        help: 'Base rotation applied to every tile, in turns.',
      },
      {
        name: 'offset',
        label: 'Offset',
        type: 'vec2',
        default: [0, 0],
        min: -1,
        max: 1,
        step: 0.001,
        group: 'Placement',
      },
      {
        name: 'sampling',
        label: 'Sampling',
        type: 'enum',
        default: 'clip',
        options: [
          { value: 'clip', label: 'Clip to cell' },
          { value: 'tile', label: 'Tile within cell' },
        ],
        group: 'Placement',
        help: 'Clip leaves empty space when a tile is shrunk or rotated. Tile repeats the pattern to fill the cell instead.',
      },
      {
        name: 'randomScale',
        label: 'Random Scale',
        type: 'float',
        default: 0,
        min: 0,
        max: 1,
        step: 0.01,
        group: 'Variation',
        help: 'Shrinks each tile by up to this fraction.',
      },
      {
        name: 'randomRotation',
        label: 'Random Rotation',
        type: 'float',
        default: 0,
        min: 0,
        max: 1,
        step: 0.01,
        group: 'Variation',
        help: '1 allows a full turn per tile.',
      },
      {
        name: 'rotationSteps',
        label: 'Rotation Steps',
        type: 'int',
        default: 0,
        min: 0,
        max: 16,
        group: 'Variation',
        help: 'Quantise random rotation. 4 gives right angles only; 0 is continuous.',
      },
      {
        name: 'randomOffset',
        label: 'Random Offset',
        type: 'float',
        default: 0,
        min: 0,
        max: 1,
        step: 0.01,
        group: 'Variation',
      },
      {
        name: 'randomFlipX',
        label: 'Random Flip X',
        type: 'bool',
        default: false,
        group: 'Variation',
        help: 'Mirrors half the tiles. Hides a repeat without rotating.',
      },
      {
        name: 'randomFlipY',
        label: 'Random Flip Y',
        type: 'bool',
        default: false,
        group: 'Variation',
      },
      {
        name: 'randomBrightness',
        label: 'Random Brightness',
        type: 'float',
        default: 0,
        min: 0,
        max: 1,
        step: 0.01,
        group: 'Variation',
      },
      {
        name: 'randomHue',
        label: 'Random Hue',
        type: 'float',
        default: 0,
        min: 0,
        max: 1,
        step: 0.01,
        group: 'Variation',
        help: 'Per-tile hue shift, in turns.',
      },
      {
        name: 'density',
        label: 'Density',
        type: 'float',
        default: 1,
        min: 0,
        max: 1,
        step: 0.01,
        group: 'Variation',
        help: 'Fraction of cells that draw at all. Below 1 punches random holes.',
      },
      {
        name: 'seed',
        label: 'Seed',
        type: 'int',
        default: 0,
        min: 0,
        max: 9999,
        group: 'Variation',
      },
    ],
    shader: /* glsl */ `
      vec4 texdir(vec2 uv) {
        vec2 counts = vec2(float(p_columns), float(p_rows));

        // Stagger from the *unshifted* indices, so row and column offsets are
        // independent instead of each feeding back into the other's index.
        //
        // The shift accumulates per row rather than alternating between two
        // states. Alternating only wraps when the count is even; accumulating a
        // snapped shift wraps for any count, and still reduces to the familiar
        // every-other-row stagger at a half-cell offset over an even grid.
        vec2 raw = uv * counts;
        float rowIndex = floor(raw.y);
        float colIndex = floor(raw.x);

        // The shift accumulates per row rather than alternating between two
        // states — alternating only wraps at an even count — and is wrapped
        // into a single cell, since whole cells of shift change nothing about
        // the layout and dropping them is what lets any count tile.
        vec2 cell = raw + vec2(
          staggerShift(rowIndex, p_rowOffset, counts.y),
          staggerShift(colIndex, p_columnOffset, counts.x)
        );

        vec2 id = floor(cell);
        vec2 f = fract(cell);

        // Hash on the wrapped id so the variation itself tiles.
        vec2 wrapped = mod(id, counts);
        vec3 r1 = hash23(wrapped + float(p_seed) * 19.3);
        vec3 r2 = hash23(wrapped.yx + float(p_seed) * 41.7 + 5.0);
        vec3 r3 = hash23(wrapped + float(p_seed) * 67.1 + 11.0);

        if (r3.z >= p_density) return vec4(0.0);

        f -= 0.5;

        // Dividing the local coordinate shrinks the sampled window, which
        // scales the tile up; invert so the parameter reads as "size".
        float scale = max(p_scale * (1.0 - p_randomScale * r2.x), 1e-4);
        f /= scale;

        float spin = r1.x;
        // Quantising before scaling keeps the steps evenly spaced.
        if (p_rotationSteps > 1) {
          spin = floor(spin * float(p_rotationSteps)) / float(p_rotationSteps);
        }
        f = rot((p_rotation + spin * p_randomRotation) * TAU) * f;

        if (p_randomFlipX && r2.y > 0.5) f.x = -f.x;
        if (p_randomFlipY && r2.z > 0.5) f.y = -f.y;

        f += 0.5;
        f += p_offset + (r1.yz - 0.5) * p_randomOffset;

        // Clip rather than wrap. Any transform that enlarges the sample window
        // — shrinking a tile, rotating it, offsetting it — pushes the
        // coordinate outside [0,1], and wrapping there repeats the whole
        // pattern inside a single cell instead of leaving the empty space the
        // transform implies. Tile mode keeps the old wrapping behaviour for
        // content that is meant to be continuous across cells.
        if (p_sampling == K_sampling_clip &&
            (f.x < 0.0 || f.x > 1.0 || f.y < 0.0 || f.y > 1.0)) {
          return vec4(0.0);
        }
        vec4 c = in0(fract(f));
        c.rgb *= 1.0 - p_randomBrightness * r3.x;
        if (p_randomHue > 0.0001) {
          vec3 hsl = rgbToHsl(saturate(c.rgb));
          hsl.x = fract(hsl.x + (r3.y - 0.5) * p_randomHue);
          c.rgb = hslToRgb(hsl);
        }
        return c;
      }
    `,
  },

  {
    id: 'filter.tileSampler',
    label: 'Tile Sampler',
    category: 'filter',
    description: 'Scatter a pattern across a grid with per-instance random transform.',
    guidance:
      'The scattering workhorse: pebbles, leaves, rivets, bricks with real variation. Unlike Tile, instances can overlap and are composited by max, so overlapping shapes read as a pile rather than a grid.',
    keywords: ['scatter', 'splatter', 'distribute', 'pebbles', 'rivets', 'random', 'instance'],
    inputs: [
      { name: 'pattern', label: 'Pattern', kind: 'color' },
      {
        name: 'mask',
        label: 'Density Mask',
        kind: 'grayscale',
        optional: true,
        fallback: [1, 1, 1, 1],
      },
    ],
    output: 'passthrough',
    passthroughFrom: 'pattern',
    params: [
      { name: 'columns', label: 'Columns', type: 'int', default: 6, min: 1, max: 64 },
      { name: 'rows', label: 'Rows', type: 'int', default: 6, min: 1, max: 64 },
      {
        name: 'rowOffset',
        label: 'Row Offset',
        type: 'float',
        default: 0,
        min: 0,
        max: 1,
        step: 0.01,
        help: 'Stagger alternating rows. 0.5 gives a hexagonal-ish packing. An odd count cannot alternate, so the shift accumulates instead and snaps to whole cells across the tile to stay seamless — which is why it steps.',
      },
      {
        name: 'size',
        label: 'Size',
        type: 'float',
        default: 1,
        min: 0.05,
        max: 4,
        step: 0.01,
        group: 'Placement',
        help: 'Relative to one cell. Above 1 the instances overlap.',
      },
      {
        name: 'sizeRatio',
        label: 'Size Ratio',
        type: 'float',
        default: 1,
        min: 0.1,
        max: 10,
        step: 0.01,
        group: 'Placement',
        help: 'Width over height. Stretches instances without stretching the grid.',
      },
      {
        name: 'rotation',
        label: 'Rotation',
        type: 'float',
        default: 0,
        min: -1,
        max: 1,
        step: 0.001,
        group: 'Placement',
        help: 'Base rotation applied to every instance, in turns.',
      },
      {
        name: 'blend',
        label: 'Blend',
        type: 'enum',
        default: 'max',
        options: [
          { value: 'max', label: 'Max' },
          { value: 'add', label: 'Add' },
          { value: 'over', label: 'Alpha over' },
        ],
        group: 'Placement',
        help: 'How overlapping instances combine. Max reads as a pile of solids.',
      },
      {
        name: 'sizeRandom',
        label: 'Size Random',
        type: 'float',
        default: 0.3,
        min: 0,
        max: 1,
        step: 0.01,
        group: 'Variation',
      },
      {
        name: 'rotationRandom',
        label: 'Rotation Random',
        type: 'float',
        default: 1,
        min: 0,
        max: 1,
        step: 0.01,
        group: 'Variation',
      },
      {
        name: 'rotationSteps',
        label: 'Rotation Steps',
        type: 'int',
        default: 0,
        min: 0,
        max: 16,
        group: 'Variation',
        help: 'Quantise random rotation. 4 gives right angles only; 0 is continuous.',
      },
      {
        name: 'positionRandom',
        label: 'Position Random',
        type: 'float',
        default: 0.3,
        min: 0,
        max: 1,
        step: 0.01,
        group: 'Variation',
      },
      {
        name: 'flipRandomX',
        label: 'Random Flip X',
        type: 'bool',
        default: false,
        group: 'Variation',
      },
      {
        name: 'flipRandomY',
        label: 'Random Flip Y',
        type: 'bool',
        default: false,
        group: 'Variation',
      },
      {
        name: 'brightnessRandom',
        label: 'Brightness Random',
        type: 'float',
        default: 0.2,
        min: 0,
        max: 1,
        step: 0.01,
        group: 'Variation',
      },
      {
        name: 'hueRandom',
        label: 'Hue Random',
        type: 'float',
        default: 0,
        min: 0,
        max: 1,
        step: 0.01,
        group: 'Variation',
        help: 'Per-instance hue shift, in turns.',
      },
      {
        name: 'density',
        label: 'Density',
        type: 'float',
        default: 1,
        min: 0,
        max: 1,
        step: 0.01,
        group: 'Variation',
        help: 'Fraction of cells that actually place an instance.',
      },
      {
        name: 'seed',
        label: 'Seed',
        type: 'int',
        default: 0,
        min: 0,
        max: 9999,
        group: 'Variation',
      },
      {
        name: 'maskMode',
        label: 'Mask Drives',
        type: 'enum',
        default: 'brightness',
        options: [
          { value: 'brightness', label: 'Brightness' },
          { value: 'density', label: 'Placement' },
          { value: 'size', label: 'Size' },
        ],
        group: 'Mask',
        help: 'What the Density Mask input controls. Placement drops instances where the mask is dark; Size shrinks them.',
      },
    ],
    shader: /* glsl */ `
      /** Contribution of the instance owning cell \`id\` to the point \`uv\`. */
      vec4 instanceAt(vec2 uv, vec2 id, vec2 counts) {
        vec2 wrapped = mod(id, counts);
        vec3 r1 = hash23(wrapped + float(p_seed) * 19.3);
        vec3 r2 = hash23(wrapped.yx + float(p_seed) * 41.7 + 5.0);
        vec3 r3 = hash23(wrapped + float(p_seed) * 67.1 + 11.0);

        if (r2.z >= p_density) return vec4(0.0);

        // Stagger alternating rows before deciding where the centre sits.
        // Accumulating a snapped shift per row, rather than alternating between
        // two states, is what lets the scatter wrap at any row count.
        //
        // The accumulated shift is taken modulo one cell before it is applied.
        // Unlike a grid, where the shift is just a coordinate transform, here it
        // moves the instance itself — and the sweep below only looks a cell or
        // two either side, so a shift approaching a full tile width would carry
        // instances clean outside the search and leave bands of nothing.
        // Whole cells of shift are exactly the part that changes nothing about
        // the layout, so dropping them costs no stagger and keeps the wrap:
        // over the full height the shift totals a whole number of cells, whose
        // fractional part repeats.
        vec2 centre = (id + 0.5 + (r1.yz - 0.5) * p_positionRandom) / counts;
        centre.x += staggerShift(id.y, p_rowOffset, counts.y) / counts.x;
        centre = fract(centre);

        float mask = in1g(centre);
        if (p_maskMode == K_maskMode_density && r3.z >= mask) return vec4(0.0);

        float scale = p_size * (1.0 - p_sizeRandom * r2.x);
        if (p_maskMode == K_maskMode_size) scale *= mask;
        if (scale < 1e-4) return vec4(0.0);

        // Wrap the delta into [-0.5, 0.5] so instances near the border reach across it.
        vec2 d = uv - centre;
        d -= floor(d + 0.5);
        d *= counts / scale;

        float spin = r1.x;
        if (p_rotationSteps > 1) {
          spin = floor(spin * float(p_rotationSteps)) / float(p_rotationSteps);
        }
        d = rot((p_rotation + spin * p_rotationRandom) * TAU) * d;

        // Aspect is applied after rotation so it stretches the instance in its
        // own frame rather than shearing the whole grid.
        d.x /= max(p_sizeRatio, 1e-3);

        if (p_flipRandomX && r2.y > 0.5) d.x = -d.x;
        if (p_flipRandomY && r3.z > 0.5) d.y = -d.y;

        vec2 local = d + 0.5;
        if (local.x < 0.0 || local.x > 1.0 || local.y < 0.0 || local.y > 1.0) return vec4(0.0);

        vec4 c = in0(local);
        c.rgb *= 1.0 - p_brightnessRandom * r3.x;
        if (p_hueRandom > 0.0001) {
          vec3 hsl = rgbToHsl(saturate(c.rgb));
          hsl.x = fract(hsl.x + (r3.y - 0.5) * p_hueRandom);
          c.rgb = hslToRgb(hsl);
        }
        if (p_maskMode == K_maskMode_brightness) c *= mask;
        return c;
      }

      vec4 texdir(vec2 uv) {
        vec2 counts = vec2(float(p_columns), float(p_rows));
        vec2 base = floor(uv * counts);

        // A neighbourhood sweep is required because \`size\` > 1 lets an instance
        // spill well outside its own cell.
        int reach = int(clamp(ceil(p_size * max(p_sizeRatio, 1.0)), 1.0, 3.0));
        vec4 acc = vec4(0.0);
        for (int y = -3; y <= 3; y++) {
          if (y < -reach) continue;
          if (y > reach) break;
          for (int x = -3; x <= 3; x++) {
            if (x < -reach) continue;
            if (x > reach) break;
            vec4 c = instanceAt(uv, base + vec2(float(x), float(y)), counts);
            if (p_blend == K_blend_add) acc += c;
            else if (p_blend == K_blend_over) acc = mix(acc, c, c.a);
            else acc = max(acc, c);
          }
        }
        return p_blend == K_blend_add ? saturate(acc) : acc;
      }
    `,
  },

  {
    id: 'filter.polar',
    label: 'Polar Transform',
    category: 'filter',
    description: 'Convert between Cartesian and polar coordinates.',
    guidance:
      'Wraps a pattern around a centre. A stripe pattern through Polar becomes a radial burst; a brick pattern becomes concentric rings. Cartesian-to-polar is also how you build gears, rosettes, and dartboards from linear input. Note this breaks tiling — polar output is radial, not periodic.',
    keywords: ['radial', 'circular', 'twirl', 'rings', 'gear', 'rosette', 'cartesian'],
    inputs: [{ name: 'input', label: 'Input', kind: 'color' }],
    output: 'passthrough',
    params: [
      {
        name: 'direction',
        label: 'Direction',
        type: 'enum',
        default: 'toPolar',
        options: [
          { value: 'toPolar', label: 'Cartesian → Polar' },
          { value: 'toCartesian', label: 'Polar → Cartesian' },
        ],
      },
      {
        name: 'center',
        label: 'Center',
        type: 'vec2',
        default: [0.5, 0.5],
        min: -1,
        max: 2,
        step: 0.001,
      },
      {
        name: 'rotation',
        label: 'Rotation',
        type: 'float',
        default: 0,
        min: -1,
        max: 1,
        step: 0.001,
      },
      {
        name: 'repeat',
        label: 'Angular Repeat',
        type: 'int',
        default: 1,
        min: 1,
        max: 32,
        help: 'Wraps the pattern this many times around the circle. The basis of gear teeth and rosettes.',
      },
      {
        name: 'radiusScale',
        label: 'Radius Scale',
        type: 'float',
        default: 1,
        min: 0.05,
        max: 8,
        step: 0.01,
      },
      {
        name: 'wrap',
        label: 'Wrap',
        type: 'enum',
        default: 'tile',
        options: WRAP_MODES,
        group: 'Sampling',
      },
    ],
    shader: /* glsl */ `
      vec4 texdir(vec2 uv) {
        vec2 p = uv - p_center;
        vec2 src;

        if (p_direction == K_direction_toPolar) {
          // Read angle along x and radius along y, so a horizontal stripe in the
          // source becomes a ring and a vertical stripe becomes a spoke.
          float angle = atan(p.y, p.x) / TAU + 0.5 + p_rotation;
          float radius = length(p) * 2.0 * p_radiusScale;
          src = vec2(angle * float(p_repeat), radius);
        } else {
          float angle = (uv.x + p_rotation) * TAU;
          float radius = uv.y * 0.5 / max(p_radiusScale, 1e-4);
          src = p_center + vec2(cos(angle), sin(angle)) * radius;
        }
        return in0m(src, p_wrap);
      }
    `,
  },

  {
    id: 'filter.blurNonUniform',
    label: 'Non-Uniform Blur',
    category: 'filter',
    description: 'Blur whose radius is driven by a map.',
    guidance:
      'Softens selectively: sharp where the mask is dark, heavily blurred where it is bright. Use it to melt one region of a pattern while leaving the rest crisp, or to fake depth of field on a height map.',
    keywords: ['variable', 'selective', 'depth of field', 'mask blur', 'melt'],
    inputs: [
      { name: 'input', label: 'Input', kind: 'color' },
      { name: 'amount', label: 'Amount Map', kind: 'grayscale' },
    ],
    output: 'passthrough',
    passthroughFrom: 'input',
    params: [
      {
        name: 'radius',
        label: 'Max Radius',
        type: 'float',
        default: 16,
        min: 0,
        max: 128,
        step: 0.1,
        help: 'Radius where the amount map is fully white, in pixels at 512px.',
      },
      {
        name: 'samples',
        label: 'Samples',
        type: 'int',
        default: 16,
        min: 4,
        max: 48,
        group: 'Quality',
      },
      { name: 'invert', label: 'Invert Map', type: 'bool', default: false },
    ],
    shader: /* glsl */ `
      vec4 texdir(vec2 uv) {
        float amount = in1g(uv);
        if (p_invert) amount = 1.0 - amount;
        float radius = p_radius * amount;
        if (radius < 0.01) return in0(uv);

        // A separable pass is impossible when the radius varies per pixel, so
        // this walks a golden-angle spiral instead — even coverage for a given
        // sample budget, and no axis-aligned streaking on directional input.
        const float GOLDEN = 2.39996323;
        int n = p_samples;
        vec4 sum = in0(uv);
        float weight = 1.0;
        for (int i = 0; i < 48; i++) {
          if (i >= n) break;
          float fi = float(i) + 0.5;
          float a = fi * GOLDEN;
          float r = sqrt(fi / float(n)) * radius;
          sum += in0l(uv + vec2(cos(a), sin(a)) * r * pixelUnit());
          weight += 1.0;
        }
        return sum / weight;
      }
    `,
  },

  {
    id: 'filter.highpass',
    label: 'Highpass',
    category: 'filter',
    description: 'Keep fine detail, discard large-scale variation.',
    guidance:
      'Flattens uneven lighting or drift while preserving texture. Essential when a colour map has broad brightness variation you did not intend — run it through Highpass and the tone evens out without losing grain.',
    keywords: ['detail', 'flatten', 'even', 'luminance', 'remove gradient', 'unsharp'],
    inputs: [{ name: 'input', label: 'Input', kind: 'color' }],
    output: 'passthrough',
    params: [
      {
        name: 'radius',
        label: 'Radius',
        type: 'float',
        default: 24,
        min: 1,
        max: 256,
        step: 0.1,
        help: 'Features larger than this radius are removed. Measured in pixels at 512px and held to the same material size at other resolutions.',
      },
      {
        name: 'intensity',
        label: 'Intensity',
        type: 'float',
        default: 1,
        min: 0,
        max: 4,
        step: 0.01,
      },
      {
        name: 'samples',
        label: 'Samples',
        type: 'int',
        default: 16,
        min: 4,
        max: 48,
        group: 'Quality',
      },
    ],
    shader: /* glsl */ `
      vec4 texdir(vec2 uv) {
        // Subtracting a blurred copy leaves exactly the frequencies above the
        // blur radius; re-centring on 0.5 keeps the result in a usable range.
        const float GOLDEN = 2.39996323;
        int n = p_samples;
        vec4 blurred = vec4(0.0);
        for (int i = 0; i < 48; i++) {
          if (i >= n) break;
          float fi = float(i) + 0.5;
          float a = fi * GOLDEN;
          float r = sqrt(fi / float(n)) * p_radius;
          blurred += in0l(uv + vec2(cos(a), sin(a)) * r * pixelUnit());
        }
        blurred /= float(n);

        vec4 src = in0(uv);
        vec3 detail = (src.rgb - blurred.rgb) * p_intensity;
        return vec4(saturate(detail + 0.5), src.a);
      }
    `,
  },

  {
    id: 'filter.safeTransform',
    label: 'Safe Transform',
    category: 'filter',
    description: 'Rotate, scale, and offset while guaranteeing the result still tiles.',
    guidance:
      'Transform 2D can break tiling at arbitrary angles and scales. This one quantises rotation to multiples of 90° and offsets to whole tiles, so whatever you do the output still wraps. Use it when tiling matters more than exact placement.',
    keywords: ['tile safe', 'seamless', 'rotate', 'scale', 'quantised', 'wrap'],
    inputs: [{ name: 'input', label: 'Input', kind: 'color' }],
    output: 'passthrough',
    params: [
      {
        name: 'quarterTurns',
        label: 'Quarter Turns',
        type: 'int',
        default: 0,
        min: 0,
        max: 3,
        help: 'Rotation in 90° steps. Arbitrary angles cannot tile, so they are not offered.',
      },
      {
        name: 'tiles',
        label: 'Tiles',
        type: 'int',
        default: 1,
        min: 1,
        max: 16,
        help: 'Whole-number repetitions, which is the only scaling that preserves the wrap.',
      },
      {
        name: 'offset',
        label: 'Offset',
        type: 'vec2',
        default: [0, 0],
        min: -1,
        max: 1,
        step: 0.001,
        help: 'Any offset is safe: a tiling texture wraps at every shift.',
      },
      { name: 'flipX', label: 'Flip X', type: 'bool', default: false },
      { name: 'flipY', label: 'Flip Y', type: 'bool', default: false },
    ],
    shader: /* glsl */ `
      vec4 texdir(vec2 uv) {
        vec2 p = fract(uv * float(p_tiles) + p_offset);

        // Quarter turns are exact swaps and negations, so they introduce no
        // resampling error and cannot break the wrap.
        int turns = int(mod(float(p_quarterTurns), 4.0));
        if (turns == 1) p = vec2(p.y, 1.0 - p.x);
        else if (turns == 2) p = vec2(1.0 - p.x, 1.0 - p.y);
        else if (turns == 3) p = vec2(1.0 - p.y, p.x);

        if (p_flipX) p.x = 1.0 - p.x;
        if (p_flipY) p.y = 1.0 - p.y;
        return in0(fract(p));
      }
    `,
  },

  {
    id: 'filter.distance',
    label: 'Distance',
    category: 'filter',
    description: 'Distance from every pixel to the nearest shape, by jump flooding.',
    guidance:
      'The node that turns a silhouette into a gradient. Feed a hard mask and get a smooth falloff away from it — the basis of bevels, of dilating or eroding a mask (pair with Histogram Scan), and of any gradient that has to follow an arbitrary outline rather than a grid. Inside mode measures depth within the shape instead.',
    keywords: [
      'distance field',
      'sdf',
      'bevel',
      'dilate',
      'erode',
      'grow',
      'shrink',
      'falloff',
      'flood',
    ],
    inputs: [{ name: 'mask', label: 'Mask', kind: 'grayscale' }],
    output: 'grayscale',
    params: [
      {
        name: 'maxDistance',
        label: 'Max Distance',
        type: 'float',
        default: 64,
        min: 2,
        max: 512,
        step: 1,
        help: 'Distance that maps to white, in pixels at 512px. Also sets the cost: the pass count grows with the base-2 logarithm of that reach in current texels.',
      },
      {
        name: 'mode',
        label: 'Mode',
        type: 'enum',
        default: 'outside',
        options: [
          { value: 'outside', label: 'Outside' },
          { value: 'inside', label: 'Inside' },
          { value: 'signed', label: 'Signed' },
        ],
        help: 'Outside grows away from the shape, Inside measures depth within it, Signed centres the boundary on 0.5.',
      },
      {
        name: 'threshold',
        label: 'Threshold',
        type: 'float',
        default: 0.5,
        min: 0,
        max: 1,
        step: 0.001,
        help: 'Mask values above this count as shape.',
      },
      { name: 'invert', label: 'Invert', type: 'bool', default: false, group: 'Output' },
    ],
    /**
     * Jump flooding: one seeding pass, then a halving sequence of propagation
     * passes. Doubling the reach each step is what makes this logarithmic in the
     * distance rather than linear — the difference between 7 passes and 64.
     */
    passes: {
      kind: 'jump-flood',
      parameter: 'maxDistance',
      minimumDistance: 2,
      extra: 2,
      minimum: 3,
      maximum: 14,
    },
    shader: /* glsl */ `
      /** True where the mask counts as shape, honouring the inside/outside sense. */
      bool isSeed(vec2 uv) {
        bool inside = in0g(uv) > p_threshold;
        return p_mode == K_mode_inside ? !inside : inside;
      }

      vec4 texdir(vec2 uv) {
        // Seeding pass: a shape pixel records its own position, everything else
        // records "no seed yet" in the blue channel.
        if (uPass == 0) {
          return isSeed(uv) ? vec4(uv, 1.0, 1.0) : vec4(0.0, 0.0, 0.0, 1.0);
        }

        vec4 best = prev(uv);
        float bestDist = best.b > 0.5 ? distance(uv, best.rg) : 1e6;

        // Step halves every pass, starting from the full search radius, and is
        // snapped to whole texels.
        //
        // The snapping is not tidiness. What travels between passes is a seed
        // *coordinate*, and the previous pass is sampled with the document's
        // filter — so an offset landing between texels returns the linear blend
        // of two neighbouring seed positions, which is a point that belongs to
        // neither of them and is usually nowhere near either. The field then
        // reads as noise. It went unnoticed because a halving sequence stays
        // integral exactly when Max Distance is a power of two: 32 and 64 were
        // clean, 26 came out as static.
        float stepPx = p_maxDistance * pow(0.5, float(uPass - 1));
        float stepTexels = max(round(stepPx * pixelUnit().x / uTexel.x), 1.0);
        vec2 stepUV = stepTexels * uTexel;

        for (int y = -1; y <= 1; y++) {
          for (int x = -1; x <= 1; x++) {
            vec4 candidate = prev(uv + vec2(float(x), float(y)) * stepUV);
            if (candidate.b < 0.5) continue;
            float d = distance(uv, candidate.rg);
            if (d < bestDist) {
              bestDist = d;
              best = candidate;
            }
          }
        }

        // Intermediate passes carry the seed coordinate forward untouched; only
        // the last one converts it to the distance the node actually outputs.
        if (uPass < uPassCount - 1) return best;

        float pixels = best.b > 0.5 ? bestDist / max(pixelUnit().x, 1e-9) : p_maxDistance;
        float v = saturate(pixels / max(p_maxDistance, 1e-4));
        if (p_mode == K_mode_signed) {
          // Inside distances run the other way from the boundary, so fold the
          // field about 0.5 using the original mask to pick the side.
          v = isSeed(uv) ? 0.5 - v * 0.5 : 0.5 + v * 0.5;
        }
        if (p_invert) v = 1.0 - v;
        return vec4(vec3(v), 1.0);
      }
    `,
  },

  {
    id: 'filter.floodFill',
    label: 'Flood Fill',
    category: 'filter',
    description: 'Label every separate shape in a mask with its bounding box.',
    guidance:
      "The expensive half of per-shape variation, and on its own it is data rather than a picture — its thumbnail is meant to look like coloured noise. Pair it with Flood Fill to Random for a flat value per shape, or Flood Fill to Gradient for a ramp inside each shape; both are single-pass and both can read the same Flood Fill, so the propagation is paid for once no matter how many ways you decode it. Seed and angle live on the decoders for exactly that reason — re-rolling a seed re-renders one cheap pass, not the march. Set Max Shape Size to roughly the width of your largest shape as a fraction of the tile — a quarter of the image is 0.25, and the pass count follows the document resolution from there so the same graph survives being scaled up. It is the one control that costs real time: reach is one pass per pixel, so the node is cheap at 512 and deliberate at 2048. Diagnosing it is easy once you know the symptom, because reach is per-shape and falls short on the biggest shape first: everything smaller still looks perfect while one or two large shapes break. Through Random that reads as per-pixel static, through Gradient as a compressed ramp with hard bands at the shape's extremes. Both mean the same thing and both go away when Max Shape Size covers the shape. Best on well-separated, compact shapes; awkwardly concave ones need more than their width suggests, since the box has to travel around the concavity rather than across it.",
    keywords: [
      'per shape',
      'random per island',
      'connected',
      'label',
      'blob',
      'variation',
      'id',
      'bounding box',
    ],
    inputs: [{ name: 'mask', label: 'Mask', kind: 'grayscale' }],
    /**
     * Four channels of per-shape data, not an image: `xy` is the bounding box's
     * low corner and `zw` its high corner, both in UV, and both allowed outside
     * [0,1] for a shape that crosses the tile seam. Background is vec4(0).
     * `floodFillValid` and `floodFillId` in the prelude are the only readers of
     * that convention, so decoders never restate it.
     */
    output: 'color',
    params: [
      {
        name: 'maxSize',
        label: 'Max Shape Size',
        type: 'float',
        default: 0.25,
        min: 0.01,
        max: 0.75,
        step: 0.01,
        help: 'Largest shape to fill, as a fraction of the tile. Sets the propagation reach and the cost with it. Too low and the biggest shapes break while smaller ones stay perfect.',
      },
      {
        name: 'threshold',
        label: 'Threshold',
        type: 'float',
        default: 0.5,
        min: 0,
        max: 1,
        step: 0.001,
      },
    ],
    /**
     * Single-pixel propagation of a per-shape bounding box, one pixel of reach
     * per pass.
     *
     * A bounding box is a happier thing to propagate than the random-key race
     * this node used to run. Componentwise min and max are associative and
     * idempotent, so every pixel of a shape converges on the same answer with no
     * tie-break and no winner to elect — and the box is strictly more
     * information, which is what lets a gradient know where the shape's extent
     * is rather than merely which shape it belongs to.
     *
     * What has not changed is that the steps must be single pixels. Three
     * approaches were tried and two do not work, which is worth recording
     * because each looks plausible. Nearest-seed jump flooding takes long jumps
     * freely because its answer has optimal substructure; propagating an extreme
     * has no such property, so sampling eight neighbours at a large radius
     * simply misses the pixel holding it whenever it lies off those rays.
     * Pointer doubling fails too: the pixel a label came from is itself the
     * local extreme, so it points at itself and the chain never lengthens.
     *
     * Gated long steps fail for a third reason: a jump the width of one shape
     * lands on the next one, and in a regular pattern the midpoint used to test
     * connectivity falls on a third — so the guard passes and two separate
     * shapes merge. Larger steps made brickwork visibly worse, not better.
     *
     * So reach is exactly one pixel per pass and cannot cross a gap, and Max
     * Shape Size buys reach and cost in equal measure. Shapes wider than that
     * come out patchy.
     */
    /**
     * Reach must cover the path from one corner of a shape to the far side of
     * it, which for an awkward outline is longer than the shape is wide. The
     * 1.6 margin is what removes the last wedge-shaped patches.
     *
     * Scaling with the resolution is the whole point. Reach is one pixel per
     * pass, so a count fixed in pixels means a node that works at 128 and
     * degrades at every size above it — the same graph, the same shapes as a
     * fraction of the tile, quietly falling apart as the document grows. Since
     * every other control in a chain like Cellular → Flood Fill is denominated
     * in features rather than pixels, this one has to be too, or changing the
     * resolution silently changes the result.
     */
    passes: {
      kind: 'resolution-reach',
      parameter: 'maxSize',
      scale: 1.6,
      extra: 8,
      minimum: 8,
    },
    shader: /* glsl */ `
      vec4 texdir(vec2 uv) {
        // Seeding is the only pass that reads the mask: every shape pixel
        // starts as a box containing only itself, and background is the zero
        // that marks it as background forever after.
        if (uPass == 0) {
          return in0g(uv) > p_threshold ? vec4(uv, uv) : vec4(0.0);
        }

        // Membership now comes from the previous pass rather than the mask. A
        // shape pixel's box always contains its own UV, so it stays valid; a
        // background pixel stays vec4(0). That is worth doing rather than
        // re-reading the mask nine times per pass, because it halves the
        // fetches in the inner loop and so buys twice the reach per unit of
        // cost — and reach is the whole ballgame for this node.
        vec4 box = prev(uv);
        if (!floodFillValid(box)) return vec4(0.0);

        // Strictly adjacent propagation. Longer steps were tried and are worse
        // than useless here: a jump the width of one brick lands on the *next*
        // brick with the mortar-free midpoint of a third in between, so the
        // connectivity test passes and two separate shapes merge under one box.
        // Moving one pixel at a time cannot cross a gap at all.
        for (int y = -1; y <= 1; y++) {
          for (int x = -1; x <= 1; x++) {
            vec4 candidate = prev(uv + vec2(float(x), float(y)) * uTexel);
            if (!floodFillValid(candidate)) continue;
            // Sampling wraps, so a neighbour across the tile seam reports a box
            // a whole tile away and a plain min/max would stretch it across the
            // image — which is what turned every seam-crossing brick into a
            // gradient spanning the entire texture. Rebasing the neighbour onto
            // the period nearest this pixel keeps a wrapped shape one shape.
            // The box then legitimately reaches outside [0,1], and pixels on
            // opposite sides of the seam settle exactly one tile apart, which is
            // the frame each of them needs.
            box.xy = min(box.xy, candidate.xy + round(box.xy - candidate.xy));
            box.zw = max(box.zw, candidate.zw + round(box.zw - candidate.zw));
          }
        }
        return box;
      }
    `,
  },

  {
    id: 'filter.floodFillRandom',
    label: 'Flood Fill to Random',
    category: 'filter',
    description: 'One flat random value per shape, from Flood Fill data.',
    guidance:
      'The per-shape variation that grid nodes cannot express: each connected blob gets one flat value, so you can drive colour, height, or roughness per shape even after the shapes have been warped or scattered. Feed the result through Gradient Map for colour. Re-rolling the seed is nearly free — the Flood Fill it reads from does not re-render.',
    keywords: ['per shape', 'random per island', 'variation', 'id', 'index', 'colour per shape'],
    inputs: [{ name: 'data', label: 'Flood Fill', kind: 'color' }],
    output: 'grayscale',
    params: [
      { name: 'seed', label: 'Seed', type: 'int', default: 0, min: 0, max: 9999 },
      {
        name: 'background',
        label: 'Background',
        type: 'float',
        default: 0,
        min: 0,
        max: 1,
        step: 0.001,
        group: 'Output',
        help: 'Value written outside every shape.',
      },
    ],
    shader: /* glsl */ `
      vec4 texdir(vec2 uv) {
        vec4 box = in0(uv);
        if (!floodFillValid(box)) return vec4(vec3(p_background), 1.0);
        return vec4(vec3(floodFillId(box, float(p_seed))), 1.0);
      }
    `,
  },

  {
    id: 'filter.floodFillGradient',
    label: 'Flood Fill to Gradient',
    category: 'filter',
    description: 'A gradient inside every shape, from Flood Fill data.',
    guidance:
      'What Flood Fill to Random cannot do: a ramp that runs across each shape individually rather than across the image. Linear at an angle gives per-brick or per-plank shading and, blended into a height map, the sense that each shape is tilted on its own; Radial gives a dome per shape, which is the quickest route to pebbles or scales. Angle Variation rotates each shape by its own random amount, which is what stops a wall of bricks from looking like one lit surface cut into pieces.',
    keywords: ['per shape gradient', 'ramp', 'bevel', 'dome', 'tilt', 'slope', 'linear', 'radial'],
    inputs: [{ name: 'data', label: 'Flood Fill', kind: 'color' }],
    output: 'grayscale',
    params: [
      {
        name: 'mode',
        label: 'Mode',
        type: 'enum',
        default: 'linear',
        options: [
          { value: 'linear', label: 'Linear' },
          { value: 'radial', label: 'Radial' },
        ],
      },
      {
        name: 'angle',
        label: 'Angle',
        type: 'float',
        default: 0,
        min: 0,
        max: 360,
        step: 1,
        help: 'Direction of the ramp, in degrees. Linear mode only.',
      },
      {
        name: 'angleVariation',
        label: 'Angle Variation',
        type: 'float',
        default: 0,
        min: 0,
        max: 1,
        step: 0.01,
        help: 'Rotates each shape by its own random amount. 1 is a full turn.',
      },
      { name: 'seed', label: 'Seed', type: 'int', default: 0, min: 0, max: 9999 },
      {
        name: 'background',
        label: 'Background',
        type: 'float',
        default: 0,
        min: 0,
        max: 1,
        step: 0.001,
        group: 'Output',
        help: 'Value written outside every shape.',
      },
    ],
    shader: /* glsl */ `
      vec4 texdir(vec2 uv) {
        vec4 box = in0(uv);
        if (!floodFillValid(box)) return vec4(vec3(p_background), 1.0);

        // Position within the shape's own box, centred on zero. A shape one
        // pixel wide would divide by zero, hence the floor on the size.
        vec2 size = max(box.zw - box.xy, vec2(1e-5));
        vec2 local = (uv - box.xy) / size - 0.5;

        float v;
        if (p_mode == K_mode_radial) {
          v = saturate(length(local) * 2.0);
        } else {
          float a = radians(p_angle);
          if (p_angleVariation > 0.0) {
            a += floodFillId(box, float(p_seed) + 3.7) * p_angleVariation * TAU;
          }
          vec2 dir = vec2(cos(a), sin(a));
          // Projecting onto an arbitrary direction spans ±0.5·(|x|+|y|) over a
          // unit box, so the extent divides back out. Without it a diagonal
          // ramp would only ever cover the middle 70% of its range and every
          // shape would read as washed out.
          v = saturate(dot(local, dir) / (abs(dir.x) + abs(dir.y)) + 0.5);
        }
        return vec4(vec3(v), 1.0);
      }
    `,
  },

  {
    id: 'filter.mirror',
    label: 'Mirror',
    category: 'filter',
    description: 'Reflect the image across an axis.',
    guidance:
      'Instant symmetry. Chain a horizontal and a vertical Mirror for four-fold kaleidoscope symmetry.',
    keywords: ['flip', 'symmetry', 'reflect', 'kaleidoscope'],
    inputs: [{ name: 'input', label: 'Input', kind: 'color' }],
    output: 'passthrough',
    params: [
      {
        name: 'axis',
        label: 'Axis',
        type: 'enum',
        default: 'x',
        options: [
          { value: 'x', label: 'Horizontal' },
          { value: 'y', label: 'Vertical' },
          { value: 'both', label: 'Both' },
        ],
      },
      {
        name: 'position',
        label: 'Position',
        type: 'float',
        default: 0.5,
        min: 0,
        max: 1,
        step: 0.001,
      },
      { name: 'flip', label: 'Flip Side', type: 'bool', default: false },
    ],
    shader: /* glsl */ `
      float foldAxis(float v, float pivot, bool flip) {
        bool past = flip ? v < pivot : v > pivot;
        return past ? 2.0 * pivot - v : v;
      }

      vec4 texdir(vec2 uv) {
        vec2 p = uv;
        if (p_axis == K_axis_x || p_axis == K_axis_both) p.x = foldAxis(p.x, p_position, p_flip);
        if (p_axis == K_axis_y || p_axis == K_axis_both) p.y = foldAxis(p.y, p_position, p_flip);
        return in0m(p, 2);
      }
    `,
  },

  {
    id: 'filter.emboss',
    label: 'Emboss',
    category: 'filter',
    description: 'Lighting-style relief from a height map.',
    guidance:
      'A quick way to preview relief, and a good source of edge highlights to blend into base colour.',
    keywords: ['relief', 'bevel', 'shade', 'highlight', 'edge'],
    inputs: [
      { name: 'input', label: 'Input', kind: 'grayscale' },
      { name: 'mask', label: 'Mask', kind: 'grayscale', optional: true, fallback: [1, 1, 1, 1] },
    ],
    output: 'grayscale',
    params: [
      {
        name: 'intensity',
        label: 'Intensity',
        type: 'float',
        default: 4,
        min: 0,
        max: 32,
        step: 0.01,
      },
      {
        name: 'angle',
        label: 'Light Angle',
        type: 'float',
        default: 0.125,
        min: -1,
        max: 1,
        step: 0.001,
      },
      {
        name: 'width',
        label: 'Width',
        type: 'float',
        default: 1,
        min: 0.25,
        max: 16,
        step: 0.01,
        help: 'Sample width in pixels at 512px, held to the same material size at other resolutions.',
      },
    ],
    shader: /* glsl */ `
      vec4 texdir(vec2 uv) {
        float a = p_angle * TAU;
        vec2 d = vec2(cos(a), sin(a)) * pixelUnit() * p_width;
        float diff = in0gl(uv + d) - in0gl(uv - d);
        float v = saturate(0.5 + diff * p_intensity * in1g(uv));
        return vec4(vec3(v), 1.0);
      }
    `,
  },

  {
    id: 'filter.sharpen',
    label: 'Sharpen',
    category: 'filter',
    description: 'Unsharp mask.',
    guidance: 'Recovers crispness after a warp or a resample. Past about 2 it starts ringing.',
    keywords: ['unsharp', 'crisp', 'detail', 'contrast'],
    inputs: [{ name: 'input', label: 'Input', kind: 'color' }],
    output: 'passthrough',
    params: [
      {
        name: 'intensity',
        label: 'Intensity',
        type: 'float',
        default: 1,
        min: 0,
        max: 4,
        step: 0.01,
      },
      {
        name: 'radius',
        label: 'Radius',
        type: 'float',
        default: 1,
        min: 0.25,
        max: 16,
        step: 0.01,
        help: 'Sample radius in pixels at 512px, held to the same material size at other resolutions.',
      },
    ],
    shader: /* glsl */ `
      vec4 texdir(vec2 uv) {
        vec2 t = pixelUnit() * p_radius;
        // 3×3 box average stands in for the Gaussian; at this radius the
        // difference is invisible and it costs a third of the taps.
        vec4 blurred = vec4(0.0);
        for (int y = -1; y <= 1; y++) {
          for (int x = -1; x <= 1; x++) {
            blurred += in0l(uv + vec2(float(x), float(y)) * t);
          }
        }
        blurred /= 9.0;
        vec4 src = in0(uv);
        return vec4(saturate(src.rgb + (src.rgb - blurred.rgb) * p_intensity), src.a);
      }
    `,
  },

  {
    id: 'filter.edge',
    label: 'Edge Detect',
    category: 'filter',
    description: 'Sobel gradient magnitude.',
    guidance:
      'Isolates boundaries. Run a pattern through this and you get an outline mask — useful for grout lines, panel seams, and dirt that collects in crevices.',
    keywords: ['sobel', 'outline', 'contour', 'boundary', 'seam'],
    inputs: [{ name: 'input', label: 'Input', kind: 'grayscale' }],
    output: 'grayscale',
    params: [
      {
        name: 'intensity',
        label: 'Intensity',
        type: 'float',
        default: 4,
        min: 0,
        max: 64,
        step: 0.01,
      },
      {
        name: 'radius',
        label: 'Radius',
        type: 'float',
        default: 1,
        min: 0.25,
        max: 16,
        step: 0.01,
        help: 'Sample radius in pixels at 512px, held to the same material size at other resolutions.',
      },
      { name: 'invert', label: 'Invert', type: 'bool', default: false, group: 'Output' },
    ],
    shader: /* glsl */ `
      vec4 texdir(vec2 uv) {
        vec2 t = pixelUnit() * p_radius;
        float tl = in0gl(uv + vec2(-t.x,  t.y));
        float tm = in0gl(uv + vec2( 0.0,  t.y));
        float tr = in0gl(uv + vec2( t.x,  t.y));
        float ml = in0gl(uv + vec2(-t.x,  0.0));
        float mr = in0gl(uv + vec2( t.x,  0.0));
        float bl = in0gl(uv + vec2(-t.x, -t.y));
        float bm = in0gl(uv + vec2( 0.0, -t.y));
        float br = in0gl(uv + vec2( t.x, -t.y));

        float gx = (tr + 2.0 * mr + br) - (tl + 2.0 * ml + bl);
        float gy = (tl + 2.0 * tm + tr) - (bl + 2.0 * bm + br);
        float v = saturate(length(vec2(gx, gy)) * p_intensity);
        if (p_invert) v = 1.0 - v;
        return vec4(vec3(v), 1.0);
      }
    `,
  },

  {
    id: 'filter.pixelate',
    label: 'Pixelate',
    category: 'filter',
    description: 'Quantise to a coarse grid.',
    guidance:
      'Mosaic and low-resolution looks, and a cheap way to build blocky masks from smooth noise.',
    keywords: ['mosaic', 'blocks', 'quantize', 'lowres', 'retro'],
    inputs: [{ name: 'input', label: 'Input', kind: 'color' }],
    output: 'passthrough',
    params: [
      { name: 'columns', label: 'Columns', type: 'int', default: 32, min: 1, max: 512 },
      { name: 'rows', label: 'Rows', type: 'int', default: 32, min: 1, max: 512 },
    ],
    shader: /* glsl */ `
      vec4 texdir(vec2 uv) {
        vec2 counts = vec2(float(p_columns), float(p_rows));
        // Sample each block's centre so the result is stable under resolution changes.
        return in0((floor(uv * counts) + 0.5) / counts);
      }
    `,
  },
];
